import { Button, Flex } from '@mantine/core';
import { useCallback, useEffect } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import {
  CONTACTS_SELECTION_MODE,
  ContactsList,
} from 'src/clients/CreateFamily/AddContactsToFamily/ContactsList';
import { FamilyList, FAMILY_SELECTION_MODE } from 'src/clients/components/FamilyList';
import { Family } from 'src/clients/hooks/useFamily';
import { FilterHeader } from 'src/common/components/table/SearchHeader';
import { Client } from 'src/users';
import { StepProps } from './DrawerFlow';
import DrawerFlowScreenLayout from './DrawerFlowScreenLayout';

type SelectionType = 'client' | 'family';

type DataWithClientOrFamily = {
  client?: Client;
  family?: Family;
  selectionType?: SelectionType;
};

type SelectClientOrFamilyStepProps<T extends DataWithClientOrFamily> = StepProps<T> & {
  footer?: React.ReactNode;
  description?: string;
};

export default function SelectClientOrFamilyStep<T extends DataWithClientOrFamily>(
  props: SelectClientOrFamilyStepProps<T>,
) {
  const { client, family, selectionType = 'client' } = props.data;
  const { client: currentClient, isClientPage, isFetching } = useCurrentClient();

  const setClient = useCallback(
    (client: Client) => {
      props.clearData();
      props.setData({
        ...props.data,
        family: undefined,
        client,
        selectionType: 'client',
      });
    },
    [props],
  );

  const setFamily = useCallback(
    (family: Family) => {
      props.clearData();
      props.setData({
        ...props.data,
        client: undefined,
        family,
        selectionType: 'family',
      });
    },
    [props],
  );

  const setSelectionType = useCallback(
    (type: SelectionType) => {
      props.setData({
        ...props.data,
        selectionType: type,
      });
    },
    [props],
  );

  useEffect(() => {
    if (currentClient && !client && !family && isClientPage && selectionType === 'client') {
      setClient(currentClient);
    }
  }, [currentClient, client, family, setClient, isClientPage, isFetching, selectionType]);

  const hasSelection = Boolean(
    (selectionType === 'client' && client) || (selectionType === 'family' && family),
  );

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select client or family"
      description={
        props.description || 'Select the client or family you wish to generate a data export for'
      }
      canGoForward={hasSelection}
      nextStep={() => props.nextStep()}
    >
      <Flex direction="column" mih={0} h="100%" style={{ flexShrink: 1, flexGrow: 0 }}>
        {selectionType === 'client' ? (
          <ContactsList
            selectedClients={client ? [client] : []}
            onSelectClient={setClient}
            selectionMode={CONTACTS_SELECTION_MODE.SINGLE}
            showAvatar={false}
            showBorder={true}
            SearchHeaderComponent={props => (
              <ClientOrFamilySearchHeader
                {...props}
                selectionType={selectionType}
                setSelectionType={setSelectionType}
              />
            )}
          />
        ) : (
          <FamilyList
            selectedFamily={family}
            onSelectFamily={setFamily}
            selectionMode={FAMILY_SELECTION_MODE.SINGLE}
            showBorder={true}
            SearchHeaderComponent={props => (
              <ClientOrFamilySearchHeader
                {...props}
                selectionType={selectionType}
                setSelectionType={setSelectionType}
              />
            )}
          />
        )}
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

type ClientOrFamilySearchHeaderProps = {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectionType: SelectionType;
  setSelectionType: (type: SelectionType) => void;
};

function ClientOrFamilySearchHeader({
  searchTerm,
  setSearchTerm,
  selectionType,
  setSelectionType,
}: ClientOrFamilySearchHeaderProps) {
  return (
    <Flex direction="column" gap="md">
      <FilterHeader
        direction="row-reverse"
        mb={0}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        inputProps={{
          maw: undefined,
        }}
      />
      <Flex gap="xs" justify="flex-start" mb="md">
        <Button
          variant={selectionType === 'client' ? 'tab-active' : 'tab-inactive'}
          onClick={() => setSelectionType('client')}
        >
          Clients
        </Button>
        <Button
          variant={selectionType === 'family' ? 'tab-active' : 'tab-inactive'}
          onClick={() => setSelectionType('family')}
        >
          Families
        </Button>
      </Flex>
    </Flex>
  );
}
